import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { getArticlesForSearch } from "src/api/helpCenter";
import { ReactComponent as EventHost } from "../../assets/HelpCenter/Event_Host2.svg";
import { ReactComponent as Performer } from "../../assets/HelpCenter/Performer2.svg";
import { ReactComponent as Crew } from "../../assets/HelpCenter/Crew2.svg";
import { ReactComponent as Planner } from "../../assets/HelpCenter/Planner2.svg";
import { useNavigate } from "react-router-dom";
import { ContactBanner } from "./ContactBanner";

const ThumbsUpIcon = () => (
  <svg
    className="w-3.5 h-3.5"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    viewBox="0 0 24 24"
  >
    <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z" />
  </svg>
);

const ArrowRightIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    viewBox="0 0 24 24"
  >
    <path d="M5 12h14M12 5l7 7-7 7" />
  </svg>
);

const SearchIcon = () => (
  <svg
    className="w-4 h-4 text-gray-400"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    viewBox="0 0 24 24"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.35-4.35" />
  </svg>
);

const ArrowLeftIcon = () => (
  <svg
    className="w-4 h-4"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    viewBox="0 0 24 24"
  >
    <path d="M19 12H5M12 5l-7 7 7 7" />
  </svg>
);

// Breadcrumb
const Breadcrumb = ({ searchTerm }) => {
  const navigate = useNavigate();

  const handleBackClick = (e) => {
    e.preventDefault();
    navigate(-1);
  };
  return (
    <div className="flex items-center gap-1.5 text-sm text-gray-500 px-8 pt-5">
      <ArrowLeftIcon />
      <button
        href="/help"
        className="hover:underline"
        onClick={handleBackClick}
      >
        Back to Help Center
      </button>
      {searchTerm && (
        <>
          <span className="text-gray-400">/</span>
          <span className="text-orange-500 font-medium">{searchTerm}</span>
        </>
      )}
    </div>
  );
};

function truncateDescription(desc) {
  if (desc.length <= 150) return desc;
  return desc.slice(0, 147) + "...";
}

function roleToIcon(role) {
  if (role === "Event Host") return <EventHost className="w-7 h-7" />;
  if (role === "Planner") return <Planner className="w-7 h-7" />;
  if (role === "Performer") return <Performer className="w-7 h-7" />;
  if (role === "Crew") return <Crew className="w-7 h-7" />;
  return null;
}

// Article Card
const ArticleCard = ({ article, searchTerm }) => {
  const navigate = useNavigate();
  const highlightTitle = (title, term) => {
    if (!term) return title;

    const regex = new RegExp(`(${term})`, "gi");
    const parts = title.split(regex);

    return (
      <>
        {parts.map((part, index) =>
          regex.test(part) ? (
            <span key={index} className="text-orange-500">
              {part}
            </span>
          ) : (
            <span key={index} className="text-gray-900">
              {part}
            </span>
          ),
        )}
      </>
    );
  };

  const handleSeeArticleClick = () => {
    navigate(`/help/${article._id}`);
  };
  return (
    <div className="border border-gray-200 rounded-xl p-5 bg-white hover:shadow-sm transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className="w-8 h-8 bg-orange-50 rounded-md flex items-center justify-center">
          {roleToIcon(article.role)}
        </div>
        <div className="flex items-center gap-1 text-sm text-gray-500">
          {article.likes}
          <ThumbsUpIcon />
        </div>
      </div>
      <h3 className="text-2xl font-semibold mb-1.5">
        {highlightTitle(article.title, searchTerm)}
      </h3>
      <p className="text-sm text-gray-500 mb-4">
        {truncateDescription(article.description)}
      </p>
      <div className="flex justify-end">
        <button
          onClick={handleSeeArticleClick}
          className="flex items-center gap-1.5 text-sm font-medium text-gray-800 hover:text-orange-500 transition-colors"
        >
          See Article
          <ArrowRightIcon />
        </button>
      </div>
    </div>
  );
};

// Main Page
export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const urlKey = searchParams.get("key") || "";

  const [allArticles, setAllArticles] = useState([]);
  const [searchTerm, setSearchTerm] = useState(urlKey);
  const [filteredArticles, setFilteredArticles] = useState([]);

  // Fetch articles on mount
  useEffect(() => {
    async function fetchArticles() {
      try {
        const arr = await getArticlesForSearch();
        setAllArticles(arr);
      } catch (error) {
        console.error("Error fetching articles:", error);
      }
    }
    fetchArticles();
  }, []);

  // Filter articles based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredArticles(allArticles);
      return;
    }

    const filtered = allArticles.filter((article) =>
      article.title.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    setFilteredArticles(filtered);
  }, [searchTerm, allArticles]);

  return (
    <div className="min-h-screen bg-white mt-20 font-sans">
      <Breadcrumb searchTerm={searchTerm} />

      <main className="max-w-5xl mx-auto px-6 pb-12">
        {/* Heading - show count only if search term exists */}
        <h1 className="text-center text-3xl font-bold text-gray-900 mt-7 mb-5">
          {searchTerm.trim() && (
            <>
              {filteredArticles.length} result
              {filteredArticles.length !== 1 ? "s" : ""} for{" "}
              <span className="text-orange-500">
                &ldquo;{searchTerm}&rdquo;
              </span>
            </>
          )}
          {!searchTerm.trim() && "All Articles"}
        </h1>

        {/* Search bar */}
        <div className="flex items-center gap-2.5 border border-gray-300 rounded-full px-5 py-2.5 max-w-md mx-auto mb-8">
          <SearchIcon />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search articles..."
            className="flex-1 text-sm outline-none text-gray-800 bg-transparent"
          />
        </div>

        {/* Article cards */}
        <div className="flex flex-col gap-3.5 min-h-lvh">
          {filteredArticles.length > 0 ? (
            filteredArticles.map((article) => (
              <ArticleCard
                key={article._id}
                article={article}
                searchTerm={searchTerm}
              />
            ))
          ) : (
            <p className="text-center text-gray-500 mt-8">No articles found.</p>
          )}
        </div>
        <ContactBanner />
      </main>
    </div>
  );
}
